import { supabase } from './supabase';
import { logger } from './logger';

interface CoachingContext {
  userId: string;
  coreValues: string[];
  lifeCircumstances: string;
  primaryGoals: any[];
  keyChallenges: string[];
  winsAndProgress: any[];
  coachingStage: 'discovery' | 'building' | 'mastery';
  relationshipStartDate: string;
  totalSessions: number;
  lastSessionDate: string;
  daysSinceLastSession: number;
}

interface ActionItem {
  id: string;
  actionText: string;
  committedDate: string;
  targetCompletionDate: string | null;
  status: 'pending' | 'completed' | 'abandoned';
}

interface RecentInsight {
  conversationDate: string;
  keyInsights: string[];
  topicsDiscussed: string[];
  followUpNeeded: string;
}

export class CoachingContextService {
  async getOrCreateContext(userId: string): Promise<CoachingContext | null> {
    try {
      let { data: context, error } = await supabase
        .from('user_coaching_context')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (!context) {
        const { data: newContext, error: insertError } = await supabase
          .from('user_coaching_context')
          .insert({
            user_id: userId,
            coaching_stage: 'discovery',
            total_sessions: 0
          })
          .select()
          .single();

        if (insertError) throw insertError;
        context = newContext;
      }

      const daysSinceLastSession = context.last_session_date
        ? Math.floor((Date.now() - new Date(context.last_session_date).getTime()) / (1000 * 60 * 60 * 24))
        : 0;

      return {
        userId: context.user_id,
        coreValues: context.core_values || [],
        lifeCircumstances: context.life_circumstances || '',
        primaryGoals: context.primary_goals || [],
        keyChallenges: context.key_challenges || [],
        winsAndProgress: context.wins_and_progress || [],
        coachingStage: context.coaching_stage || 'discovery',
        relationshipStartDate: context.relationship_start_date,
        totalSessions: context.total_sessions || 0,
        lastSessionDate: context.last_session_date,
        daysSinceLastSession
      };
    } catch (error) {
      logger.error('Error getting coaching context:', error);
      return null;
    }
  }

  async getPendingActionItems(userId: string, limit: number = 5): Promise<ActionItem[]> {
    try {
      const { data, error } = await supabase
        .from('coaching_action_items')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'pending')
        .order('committed_date', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Error getting action items:', error);
      return [];
    }
  }

  async getRecentInsights(userId: string, limit: number = 3): Promise<RecentInsight[]> {
    try {
      const { data, error } = await supabase
        .from('coaching_conversation_summaries')
        .select('*')
        .eq('user_id', userId)
        .order('conversation_date', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Error getting recent insights:', error);
      return [];
    }
  }

  async getActiveActionPlans(userId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('user_action_plans')
        .select('id, goal, progress_percentage, timeline, created_at')
        .eq('user_id', userId)
        .in('status', ['in_progress', 'not_started'])
        .order('created_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Error getting action plans:', error);
      return [];
    }
  }

  async getUserDistractions(userId: string): Promise<string[]> {
    try {
      const { data, error } = await supabase
        .from('user_distractions')
        .select('distraction_type')
        .eq('user_id', userId)
        .eq('goal_status', 'active');

      if (error) throw error;
      return data?.map((d: any) => d.distraction_type) || [];
    } catch (error) {
      logger.error('Error getting user distractions:', error);
      return [];
    }
  }

  async getRecentJournalEntries(userId: string, limit: number = 5): Promise<Array<{
    date: string;
    mood: string;
    content: string;
    tags: string[];
  }>> {
    try {
      const { data, error } = await supabase
        .from('journal_entries')
        .select('date, mood, quick_entry, win_of_day, challenge_faced, tags, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return (data || []).map((entry: any) => ({
        date: entry.date,
        mood: entry.mood || 'neutral',
        content: entry.quick_entry || entry.win_of_day || entry.challenge_faced || '',
        tags: entry.tags || []
      })).filter((e: any) => e.content);
    } catch (error) {
      logger.error('Error getting recent journal entries:', error);
      return [];
    }
  }

  async buildContextPrompt(userId: string, _topicId?: string): Promise<string> {
    const context = await this.getOrCreateContext(userId);
    if (!context) return '';

    const actionItems = await this.getPendingActionItems(userId);
    const recentInsights = await this.getRecentInsights(userId);
    const actionPlans = await this.getActiveActionPlans(userId);
    const distractions = await this.getUserDistractions(userId);
    const journalEntries = await this.getRecentJournalEntries(userId, 3);

    let contextPrompt = '\n\n--- COACHING RELATIONSHIP CONTEXT ---\n';

    // Relationship stage and history
    if (context.totalSessions === 0) {
      contextPrompt += `This is your first conversation with this person. Focus on building rapport, understanding who they are, and establishing trust. Be warm and welcoming.\n`;
    } else if (context.totalSessions < 5) {
      contextPrompt += `You've had ${context.totalSessions} conversation${context.totalSessions > 1 ? 's' : ''} with them (coaching stage: ${context.coachingStage}). You're still getting to know them. Build on what you've learned.\n`;
    } else {
      contextPrompt += `You've had ${context.totalSessions} coaching conversations over ${this.getTimeDescription(context.relationshipStartDate)}. They're in the "${context.coachingStage}" stage. Reference your ongoing relationship naturally.\n`;
    }

    // Time since last session
    if (context.daysSinceLastSession > 0) {
      contextPrompt += `It's been ${context.daysSinceLastSession} day${context.daysSinceLastSession > 1 ? 's' : ''} since your last conversation.`;
      if (context.daysSinceLastSession > 7) {
        contextPrompt += ` Welcome them back warmly and check in on how things have been.\n`;
      } else {
        contextPrompt += `\n`;
      }
    }

    // Core values and circumstances
    if (context.coreValues.length > 0) {
      contextPrompt += `\nTheir stated values: ${context.coreValues.join(', ')}\n`;
    }
    if (context.lifeCircumstances) {
      contextPrompt += `Current situation: ${context.lifeCircumstances}\n`;
    }

    // Current distractions and habits to break
    if (distractions.length > 0) {
      contextPrompt += `\nIMPORTANT - Known distractions/time-wasters they're working to overcome: ${distractions.join(', ')}\n`;
      contextPrompt += `DO NOT suggest careers related to these distractions. If they mention interest in careers involving these areas (e.g., gaming -> streamer/tester, social media -> influencer), actively redirect them to explore completely different fields. Challenge them to think beyond their comfort zone.\n`;
    }

    // Goals and challenges
    if (context.primaryGoals.length > 0) {
      contextPrompt += `\nMain goals they're working toward:\n`;
      context.primaryGoals.forEach((goal: any, index: number) => {
        contextPrompt += `${index + 1}. ${typeof goal === 'string' ? goal : goal.text || JSON.stringify(goal)}\n`;
      });
    }

    if (context.keyChallenges.length > 0) {
      contextPrompt += `\nChallenges they're facing: ${context.keyChallenges.join(', ')}\n`;
    }

    // Recent wins
    if (context.winsAndProgress.length > 0) {
      const recentWins = context.winsAndProgress.slice(0, 3);
      contextPrompt += `\nRecent progress and wins:\n`;
      recentWins.forEach((win: any, _idx: number) => {
        contextPrompt += `- ${typeof win === 'string' ? win : win.text || JSON.stringify(win)}\n`;
      });
    }

    // Pending action items (accountability)
    if (actionItems.length > 0) {
      contextPrompt += `\nThey committed to these actions:\n`;
      actionItems.forEach((item, index) => {
        const daysAgo = Math.floor((Date.now() - new Date(item.committedDate).getTime()) / (1000 * 60 * 60 * 24));
        contextPrompt += `${index + 1}. "${item.actionText}" (${daysAgo} day${daysAgo !== 1 ? 's' : ''} ago)\n`;
      });
      contextPrompt += `Gently check in on one or two of these if relevant to the conversation. Be supportive if they didn't follow through.\n`;
    }

    // Recent conversation insights
    if (recentInsights.length > 0 && recentInsights[0].keyInsights?.length > 0) {
      contextPrompt += `\nKey insights from recent conversations:\n`;
      recentInsights[0].keyInsights.forEach((insight: string) => {
        contextPrompt += `- ${insight}\n`;
      });
    }

    // Follow-up needed
    if (recentInsights.length > 0 && recentInsights[0].followUpNeeded) {
      contextPrompt += `\nFollow up on: ${recentInsights[0].followUpNeeded}\n`;
    }

    // Active action plans
    if (actionPlans.length > 0) {
      contextPrompt += `\nActive Action Plans:\n`;
      actionPlans.forEach((plan, index) => {
        const daysAgo = Math.floor((Date.now() - new Date(plan.created_at).getTime()) / (1000 * 60 * 60 * 24));
        contextPrompt += `${index + 1}. "${plan.goal}" - ${plan.progress_percentage}% complete`;
        if (plan.timeline) {
          contextPrompt += ` (${plan.timeline})`;
        }
        contextPrompt += ` - created ${daysAgo} day${daysAgo !== 1 ? 's' : ''} ago\n`;
      });
      contextPrompt += `You can reference these plans, check in on progress, and offer to help adjust them if needed. If they already have multiple plans, be mindful before suggesting new ones.\n`;
    }

    // Recent journal entries
    if (journalEntries.length > 0) {
      contextPrompt += `\nRecent journal entries (private reflections they've shared):\n`;
      journalEntries.forEach((entry, index) => {
        const entryDate = new Date(entry.date);
        const isToday = entryDate.toDateString() === new Date().toDateString();
        const dateLabel = isToday ? 'Today' : entryDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        contextPrompt += `${index + 1}. [${dateLabel}] "${entry.content.substring(0, 100)}${entry.content.length > 100 ? '...' : ''}"`;
        if (entry.mood && entry.mood !== 'neutral') {
          contextPrompt += ` (mood: ${entry.mood})`;
        }
        if (entry.tags.length > 0) {
          contextPrompt += ` [tags: ${entry.tags.join(', ')}]`;
        }
        contextPrompt += '\n';
      });
      contextPrompt += `Use these journal insights to understand what's on their mind. Reference specific things they wrote about when relevant - it shows you're paying attention.\n`;
    }

    contextPrompt += `\n--- END CONTEXT ---\n\n`;
    contextPrompt += `Use this context naturally in your responses. Don't list it out - weave it into the conversation to show you remember and care about their journey.\n`;

    return contextPrompt;
  }

  async updateSessionCount(userId: string): Promise<void> {
    try {
      const { data: context } = await supabase
        .from('user_coaching_context')
        .select('total_sessions')
        .eq('user_id', userId)
        .maybeSingle();

      const currentCount = context?.total_sessions || 0;

      await supabase
        .from('user_coaching_context')
        .update({
          total_sessions: currentCount + 1,
          last_session_date: new Date().toISOString()
        })
        .eq('user_id', userId);
    } catch (error) {
      logger.error('Error updating session count:', error);
    }
  }

  async saveConversationSummary(
    userId: string,
    topicId: string | null,
    insights: string[],
    topicsDiscussed: string[],
    followUpNeeded: string = ''
  ): Promise<void> {
    try {
      await supabase
        .from('coaching_conversation_summaries')
        .insert({
          user_id: userId,
          coaching_topic_id: topicId,
          key_insights: insights,
          topics_discussed: topicsDiscussed,
          follow_up_needed: followUpNeeded
        });
    } catch (error) {
      logger.error('Error saving conversation summary:', error);
    }
  }

  private getTimeDescription(startDate: string): string {
    const days = Math.floor((Date.now() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24));
    if (days < 7) return `${days} day${days !== 1 ? 's' : ''}`;
    if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) !== 1 ? 's' : ''}`;
    if (days < 365) return `${Math.floor(days / 30)} month${Math.floor(days / 30) !== 1 ? 's' : ''}`;
    return `${Math.floor(days / 365)} year${Math.floor(days / 365) !== 1 ? 's' : ''}`;
  }
}

export const coachingContext = new CoachingContextService();
